import { AppConstants } from '@unpispas/upp-defs';
import { PriceInfo, PriceType } from '@unpispas/upp-defs';
import { dataService } from '../../data';

import { DataObject, ObjectOptions } from "../base";
import { CreateObject } from "../item";

import { Ticket } from './ticket';
import { TicketType } from '../types';
import { TicketView } from '../views/ticket';
import { PosDevice } from './device';
import { Ticketbai } from './ticketbai';
import { Verifactu } from './verifactu';
import { I18nValues } from './i18nvalue';

import { TicketProductView } from '../views/ticketproduct';
import { TicketOfferView } from '../views/ticketoffer';

const TICKET_DATA_SCHEMA_VERSION = 3;

const _schema = {
    "name": "TICKETDATA",
    "fields": [
        { name: "ticket", type: "objid" },
        { name: "registry", type: "string" },
        { name: "reg_ver", type: "number" }
    ],
    "relate": [
        { direction: ">", target: "TICKET", by: "ticket", name: "ticket", reason: "ticket", child: false, class: TicketType }
    ]
} as const;

const _TicketDataClass = CreateObject(_schema);
if (!_TicketDataClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/*******************************************************/
/* TYPES                                               */
/*******************************************************/

export type TicketDataTaxEntry = {
    rate: number;
    base: number;
    amount: number;
    total: number;
};

export type TicketDataAmountsEntry = {
    base: number;
    amount: number;
    total: number;
    taxes: TicketDataTaxEntry[];
};

export type TicketDataTicketRef = {
    objid: number | null;
    place: number | null;
    qrcode: number | null;
    source: string | null;
    orderno: string | null;
    series: string | null;
    invceno: string | null;
    status: string | null;
};

export type TicketDataFiscalSubmission = {
    objid: number | null;
    attempts: number;
    sentAt: string | null;
    validCode: string | null;
    errorCode: string | null;
};

export type TicketDataFiscalSystem = 'TICKETBAI' | 'VERIFACTU' | null;
export type TicketDataFiscalStatus = 'SENT_OK' | 'SENT_KO' | 'READY' | 'DRAFT';

export type TicketDataFiscalInfo = {
    system: TicketDataFiscalSystem;
    status: TicketDataFiscalStatus;
    region: string | null;
    issuedAt: string | null;
    submission: TicketDataFiscalSubmission;
    qr?: {
        ticketbai: {
            id: string | null;
            qr: string | null;
        } | null;
        verifactu: {
            qr: string | null;
        } | null;
    };
};

export type TicketDataChangeEntry = {
    date: string | null;
    reason: string | null;
    series: string | null;
    invoice: string | null;
    total: number | null;
    base: number | null;
    taxes: number | null;
    signature: string | null;
};

export type TicketDataMerchantInfo = {
    name: string | null;
    taxId: string | null;
};

/** Invoice recipient stored in the ticket registry (ISO country code). */
export type TicketDataRecipientInfo = {
    name: string | null;
    taxId: string | null;
    country: string | null;
};

/**
 * Catalog name in the ticket registry:
 * - `label`: place-language text (`name.tr(place.lang)`), used by print/fiscal today
 * - `values`: full i18n map (same shape as before the freeze), kept for future use
 */
export type TicketDataName = {
    label: string | null;
    values: I18nValues | null;
};

export type TicketDataShortcutInfo = {
    objid: number | null;
    name: TicketDataName;
    code: string | null;
};

export type TicketDataOptionInfo = {
    objid: number | null;
    group: TicketDataName;
    name: TicketDataName;
    price?: number;
    total?: number;
};

export type TicketDataLineEntry = {
    objid: number | null;
    offer: number | null;
    name: TicketDataName;
    qty: number;
    shortcut: TicketDataShortcutInfo | null;
    options: TicketDataOptionInfo[];
    print: {
        refund: boolean;
        cancel: boolean;
        fixed: boolean;
        isweight: boolean;
        price: number;
        charge: number;
        house: boolean;
        adjust: number;
    };
    amounts: TicketDataAmountsEntry;
};

export type TicketDataOfferEntry = {
    objid: number | null;
    name: TicketDataName;
    isMenu: boolean;
    qty?: number;
    total?: number;
    appliesTo: number[];
};

export type TicketDataChargeEntry = {
    objid: number | null;
    name: TicketDataName;
    amounts: TicketDataAmountsEntry;
};

export type TicketDataPaymentEntry = {
    method: string | null;
    amount: number;
    given: number | null;
    change: number | null;
};

export type TicketDataTillInfo = {
    objid: number | null;
    name: string | null;
};

export type TicketDataPaidinInfo = {
    name: string | null;
};

export type TicketDataCommercialState = 'ACTIVE' | 'CANCEL' | 'DELETE';

export type TicketDataActivityEntry = {
    amount: number;
    income: number;
};

export type TicketDataCommercialInfo = {
    state: TicketDataCommercialState;
    lines: TicketDataLineEntry[];
    offers: TicketDataOfferEntry[];
    extras: TicketDataChargeEntry[];
    discounts: TicketDataChargeEntry[];
    payments: TicketDataPaymentEntry[];
    totals: TicketDataAmountsEntry;
    addlines: TicketDataActivityEntry;
    dellines: TicketDataActivityEntry;
};

export type TicketDataRegistry = {
    schemaVersion: number;
    ticketRef: TicketDataTicketRef;
    fiscal: TicketDataFiscalInfo;
    changes: TicketDataChangeEntry[];
    merchant: TicketDataMerchantInfo;
    recipient: TicketDataRecipientInfo | null;
    commercial: TicketDataCommercialInfo;
    till?: TicketDataTillInfo | null;
    paidin?: TicketDataPaidinInfo | null;
};

/*******************************************************/
/* CLASS                                               */
/*******************************************************/

export class TicketData extends _TicketDataClass {
    private _registryCache: TicketDataRegistry | null = null;
    private _registryLoaded = false;

    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): TicketData {
        return this._Copy(store) as TicketData;
    }

    protected override DoChildLinked(relation: string | null, related: DataObject | null): void {
        if (relation !== 'ticket' || !(related instanceof Ticket)){
            return;
        }

        if (this._children['ticket'] === related){
            related.AddData(this);
        }
        else {
            related.DelData(this);
        }
    }

    get Registry(): TicketDataRegistry | null {
        if (this._registryLoaded){
            return this._registryCache;
        }

        this._registryLoaded = true;

        const _raw = this['__base__registry'] as string | null;
        if (!_raw){
            this._registryCache = null;
            return null;
        }

        try {
            this._registryCache = JSON.parse(_raw) as TicketDataRegistry;
        }
        catch {
            this._registryCache = null;
        }

        return this._registryCache;
    }

    set registry(value: string | null){
        if (this['__base__registry'] === value){
            return;
        }

        this['__base__registry'] = value;
        this._registryLoaded = false;
        this._registryCache = null;
        this.ToUpdate = true;
    }

    Refresh(ticket: TicketView): TicketDataRegistry {
        const _registry = this.BuildRegistry(ticket);
        this.ticket = ticket.object;
        this.registry = JSON.stringify(_registry);
        this.reg_ver = _registry.schemaVersion;
        return _registry;
    }

    private BuildRegistry(ticket: TicketView): TicketDataRegistry {
        void ticket.priceinfo;

        const _placeLang = this.PlaceLang(ticket);
        const _invoiceClient = ticket.invoice?.client as any;
        const _customerName = this._strornull(_invoiceClient?.name || _invoiceClient?.business);
        const _customerTaxId = this._strornull(_invoiceClient?.taxid || _invoiceClient?.taxId);
        const _customerCountry = this._countryCode(_invoiceClient?.country);
        const _isPaid = ticket.IsPaid;
        const _isCancelled = ticket.IsCancel;
        const _isErroneous = _isCancelled && ticket.iserror;
        const _totals = _isCancelled
            ? this.ZeroAmounts()
            : this.AmountsFromPrice(ticket.priceinfo);
        const _system = this.FiscalSystem(ticket);
        const _submission = this.FiscalSubmission(ticket);
        const _sentOk = (!!_submission.sentAt && !_submission.errorCode);
        const _sentKo = (!!_submission.sentAt && !!_submission.errorCode);
        const _commercialState: TicketDataCommercialState = _isErroneous
            ? 'DELETE'
            : (_isCancelled ? 'CANCEL' : 'ACTIVE');
        const _fiscalStatus = _sentOk
            ? 'SENT_OK'
            : _sentKo
                ? 'SENT_KO'
                : (_isPaid ? 'READY' : 'DRAFT');
        const _lineRefByProduct = new Map<string, number>();
        const _offerRefByOffer = new Map<string, number>();
        const _commercialProducts = ticket.products.filter((line) => !line.IsSplitter);
        if (!_isCancelled){
            _commercialProducts.forEach((line, index) => {
                const _objid = this._numornull(line.objid);
                const _lineRef = this.ViewReferenceKey(line);
                if (_lineRef){
                    _lineRefByProduct.set(_lineRef, _objid ?? -(index + 1));
                }
            });
            ticket.offers.forEach((offer, index) => {
                const _objid = this._numornull(offer.objid);
                const _offerRef = this.ViewReferenceKey(offer);
                if (_offerRef){
                    _offerRefByOffer.set(_offerRef, _objid ?? -(index + 1));
                }
            });
        }
        const _lines = _isCancelled ? [{
            objid: null,
            offer: null,
            name: {
                label: 'Rectificacion por cancelacion',
                values: null,
            },
            qty: 1,
            shortcut: null,
            options: [],
            print: {
                refund: false,
                cancel: true,
                fixed: false,
                isweight: false,
                price: 0,
                charge: 0,
                house: false,
                adjust: 0,
            },
            amounts: this.ZeroAmounts(),
        }] : _commercialProducts.map((line) => this.BuildLine(line, _offerRefByOffer, _placeLang));
       
        // Emit display heads only: cart already groups via DisplayTo/DisplayFr.
        const _offers = _isCancelled ? [] : ticket.offers
            .filter((offer) => !offer.DisplayTo)
            .map((offer) => this.BuildOfferEntry(offer, _offerRefByOffer, _lineRefByProduct, _placeLang));
        
            const _extras = _isCancelled ? [] : ticket.extras.map((extra) => {
            const _name = extra.extra?.name;
            return {
                objid: this._numornull(extra.objid),
                name: {
                    label: String(_name?.tr(_placeLang) || '').trim() || null,
                    values: _name?.values ?? null,
                },
                amounts: this.AmountsFromPrice(extra.chargeinfo),
            };
        });
        
        const _discounts = _isCancelled ? [] : ticket.discounts.map((discount) => {
            const _name = discount.discount?.name;
            return {
                objid: this._numornull(discount.objid),
                name: {
                    label: String(_name?.tr(_placeLang) || '').trim() || null,
                    values: _name?.values ?? null,
                },
                amounts: this.DiscountAmounts(ticket, discount.charge || 0),
            };
        });
        
        const _payments = _isCancelled ? [] : this.BuildPayments(ticket);
        const _ticketBai = this.ResolveFiscalTicketBai(ticket);
        const _verifactu = this.ResolveFiscalVerifactu(ticket);

        const _changes: TicketDataChangeEntry[] = ticket.changes.map((change) => ({
            date: change.created?.toISOString() || null,
            reason: change.reason || null,
            series: change.series || null,
            invoice: change.invoice || null,
            total: this._round(change.total || 0) ?? 0,
            base: this._round(change.base || 0) ?? 0,
            taxes: this._round(change.taxes || 0) ?? 0,
            signature: this.ChangeChainSignature(change)
        }));

        return {
            schemaVersion: TICKET_DATA_SCHEMA_VERSION,

            ticketRef: {
                objid: this._numornull(ticket.objid),
                place: this._numornull(ticket.place?.objid),
                qrcode: this._numornull(ticket.qrcode?.objid),
                source: this._strornull(ticket.source),
                orderno: ticket.orderno || null,
                series: ticket.series || null,
                invceno: ticket.invceno || null,
                status: ticket.status || null
            },

            fiscal: {
                system: _system,
                status: _fiscalStatus,
                region: ticket.place?.fiscal?.province || null,
                issuedAt: ticket.lastchange?.created?.toISOString()
                    || ticket.created?.toISOString()
                    || ticket.updated?.toISOString()
                    || null,
                submission: _submission,
                qr: {
                    ticketbai: _ticketBai ? {
                        id: String(_ticketBai.id || '').trim() || null,
                        qr: String(_ticketBai.qr || '').trim() || null,
                    } : null,
                    verifactu: _verifactu ? {
                        qr: String(_verifactu.qr || '').trim() || null,
                    } : null,
                },
            },

            changes: _changes,

            merchant: {
                name: ticket.place?.name || null,
                taxId: ticket.place?.taxid || null
            },

            recipient: ticket.invoice ? {
                name: _customerName,
                taxId: _customerTaxId,
                country: _customerCountry,
            } : null,

            commercial: {
                state: _commercialState,
                lines: _lines,
                offers: _offers,
                extras: _extras,
                discounts: _discounts,
                payments: _payments,
                totals: _totals,
                // Keep cart activity on cancelled tickets so removed lines still
                // feed cash-close "líneas eliminadas" (anti-fraud / till report).
                addlines: this._roundLineActivityEntry(ticket.lineActivity.addlines),
                dellines: this._roundLineActivityEntry(ticket.lineActivity.dellines),
            },

            till: this.BuildTillInfo(ticket),
            paidin: this.BuildPaidinInfo(ticket),
        };
    }

    private BuildPaidinInfo(ticket: TicketView): TicketDataPaidinInfo | null {
        if (!ticket.IsPaid){
            return null;
        }

        const _session = ticket.paidin;
        if (!_session){
            return null;
        }

        const _device = _session.device;
        if (_device instanceof PosDevice){
            return {
                name: this._strornull(_device.name),
            };
        }

        return { name: null };
    }

    private BuildTillInfo(ticket: TicketView): TicketDataTillInfo | null {
        if (!ticket.IsPaid){
            return null;
        }

        const _till = ticket.till;
        if (!_till){
            return null;
        }

        const _tillName = this._strornull(String(_till.name ?? '').trim());
        return {
            objid: this._numornull(_till.objid),
            name: _tillName,
        };
    }

    private FiscalSystem(ticket: TicketView): TicketDataFiscalSystem {
        if (ticket.place?.SendBAIEnabled){
            return 'TICKETBAI';
        }
        if (ticket.place?.SendVFTEnabled){
            return 'VERIFACTU';
        }
        return null;
    }

    /** Newest TicketChange that already carries a TicketBAI row (payment QR first). */
    private ResolveFiscalTicketBai(ticket: TicketView): Ticketbai | null {
        if (ticket.lastchange?.ticketbai){
            return ticket.lastchange.ticketbai;
        }

        const _changes = ticket.changes || [];
        for (let index = _changes.length - 1; index >= 0; index--){
            const _entry = _changes[index]?.ticketbai;
            if (_entry){
                return _entry;
            }
        }

        return null;
    }

    /** Newest TicketChange that already carries a VeriFactu row. */
    private ResolveFiscalVerifactu(ticket: TicketView): Verifactu | null {
        if (ticket.lastchange?.verifactu){
            return ticket.lastchange.verifactu;
        }

        const _changes = ticket.changes || [];
        for (let index = _changes.length - 1; index >= 0; index--){
            const _entry = _changes[index]?.verifactu;
            if (_entry){
                return _entry;
            }
        }

        return null;
    }

    /**
     * Registry chain fingerprint for a change: VeriFactu Huella when present,
     * otherwise TicketBAI SignatureValue (normalized, first 100 chars).
     */
    private ChangeChainSignature(change: { verifactu?: Verifactu | null; ticketbai?: Ticketbai | null }): string | null {
        const _vftu = String(change.verifactu?.sign || '').trim();
        if (_vftu){
            return _vftu;
        }

        const _tbai = String(change.ticketbai?.sign || '').replace(/\s+/g, '');
        return _tbai ? _tbai.substring(0, 100) : null;
    }

    private FiscalSubmission(ticket: TicketView): TicketDataFiscalSubmission {
        const _ticketBai = this.ResolveFiscalTicketBai(ticket);
        const _verifactu = this.ResolveFiscalVerifactu(ticket);
        const _entry = _ticketBai || _verifactu;

        return {
            objid: this._numornull(_entry?.objid),
            attempts: (_entry?.sent || _entry?.error || _entry?.valid) ? 1 : 0,
            sentAt: _entry?.sent?.toISOString() || null,
            validCode: _entry?.valid || null,
            errorCode: _entry?.error || null
        };
    }

    /** Place print/fiscal language used as the default `label` in catalog name snapshots. */
    private PlaceLang(ticket: TicketView): string {
        const _raw = String(ticket.place?.lang || 'ES').trim().toUpperCase();
        return _raw || 'ES';
    }

    private BuildLine(
        line: TicketProductView,
        offerRefByOffer: Map<string, number>,
        placeLang: string,
    ): TicketDataLineEntry {
        const _qty = Number(line.amount || 0);
        const _calcPrice = Number(line.calcinfo.price || 0);
        const _calcCharge = Number(line.calcinfo.charge || 0);
        const _calcRefund = Number(line.calcinfo.refund || 0);
        const _isRefund = _calcRefund > 0;
        const _isCancel = line.status === 'DE';
        const _isFixed = line.calcinfo.fixed !== null;
        const _chargeTotal = _isRefund
            ? -Math.abs(this._round(_calcRefund * _qty) || 0)
            : this._round(_calcCharge * _qty) || 0;
        const _total = _chargeTotal;
        const _baseCharge = this._round(_calcPrice * _qty) || 0;
        const _adjust = _isFixed
            ? (this._round(_chargeTotal - _baseCharge) || 0)
            : 0;
        const _house = _isFixed && (_chargeTotal === 0);
        const _hasOffer = line.offer != null;
        const _displayCharge = (_isFixed || _hasOffer) ? _baseCharge : _chargeTotal;
        const _rate = line.taxrate ?? AppConstants.defaultTaxRate;
        const _amounts = this.AmountsFromPrice(new PriceInfo(_total, _rate).price);
        const _shortcut = line.shortcut;
        const _productName = line.product?.name;

        return {
            objid: this._numornull(line.objid),
            offer: this.ResolveOfferReference(line.offer, offerRefByOffer),
            name: {
                label: String(_productName?.tr(placeLang) || '').trim() || null,
                values: _productName?.values ?? null,
            },
            qty: _qty,
            shortcut: _shortcut
                ? {
                    objid: this._numornull(_shortcut.objid),
                    name: {
                        label: String(_shortcut.name?.tr(placeLang) || '').trim() || null,
                        values: _shortcut.name?.values ?? null,
                    },
                    code: _shortcut.code || null
                }
                : null,
            options: line.options.map((option) => {
                const _optionName = option.name;
                const _groupName = option.category?.name;
                return {
                    objid: this._numornull(option.objid),
                    group: {
                        label: String(_groupName?.tr(placeLang) || '').trim() || null,
                        values: _groupName?.values ?? null,
                    },
                    name: {
                        label: String(_optionName?.tr(placeLang) || '').trim() || null,
                        values: _optionName?.values ?? null,
                    },
                    price: Number(option.price || 0),
                    total: this._round((line.amount || 0) * Number(option.price || 0)) || 0,
                };
            }),
            print: {
                refund: _isRefund,
                cancel: _isCancel,
                fixed: _isFixed,
                isweight: !!line.product?.IsWeighted,
                price: _calcPrice,
                charge: _displayCharge,
                house: _house,
                adjust: _adjust,
            },
            amounts: _amounts,
        };
    }

    private BuildOfferEntry(
        offer: TicketOfferView,
        offerRefByOffer: Map<string, number>,
        lineRefByProduct: Map<string, number>,
        placeLang: string,
    ): TicketDataOfferEntry {
        let _qty = this.OfferUnits(offer);
        let _total = Number(offer.calcinfo.charge || 0);
        const _appliesTo = this.OfferAppliesTo(offer, lineRefByProduct);

        let _grouped = offer.DisplayFr;
        while (_grouped){
            _qty += this.OfferUnits(_grouped);
            _total += Number(_grouped.calcinfo.charge || 0);
            for (const _ref of this.OfferAppliesTo(_grouped, lineRefByProduct)){
                if (!_appliesTo.includes(_ref)){
                    _appliesTo.push(_ref);
                }
            }
            _grouped = _grouped.DisplayFr;
        }

        const _offerName = offer.offer?.name;
        return {
            objid: this.ResolveOfferReference(offer, offerRefByOffer),
            name: {
                label: String(_offerName?.tr(placeLang) || '').trim() || null,
                values: _offerName?.values ?? null,
            },
            isMenu: offer.offer?.IsMenu || false,
            qty: _qty,
            total: this._round(_total) ?? 0,
            appliesTo: _appliesTo,
        };
    }

    private OfferUnits(offer: TicketOfferView): number {
        const _raw = Number(offer.Count);
        if (Number.isFinite(_raw) && (_raw > 0)){
            return _raw;
        }
        return 1;
    }

    private OfferAppliesTo(offer: TicketOfferView, lineRefByProduct: Map<string, number>): number[] {
        return offer.products
            .map((product) => {
                const _productRef = this.ViewReferenceKey(product);
                return (_productRef ? lineRefByProduct.get(_productRef) : null) ?? this._numornull(product.objid);
            })
            .filter((ref): ref is number => ref != null);
    }

    private ResolveOfferReference(offer: TicketOfferView | null, offerRefByOffer: Map<string, number>): number | null {
        if (!offer){
            return null;
        }

        // Point line.offer at the display head so print filters menu products correctly.
        let _head = offer;
        while (_head.DisplayTo){
            _head = _head.DisplayTo;
        }

        const _objid = this._numornull(_head.objid);
        if (_objid != null){
            return _objid;
        }

        const _offerRef = this.ViewReferenceKey(_head);
        if (!_offerRef){
            return null;
        }

        return offerRefByOffer.get(_offerRef) ?? null;
    }

    private ViewReferenceKey(view: { objid?: string | number | null; object?: any; _uuid?: string | null } | null): string | null {
        if (!view){
            return null;
        }

        const _objid = this._numornull(view.objid ?? null);
        if (_objid != null){
            return `objid:${_objid}`;
        }

        const _rawUuid = String(view.object?._uuid || view._uuid || '').trim();
        if (_rawUuid.length > 0){
            return `uuid:${_rawUuid}`;
        }

        return null;
    }

    private BuildPayments(ticket: TicketView): TicketDataPaymentEntry[] {
        if (!ticket.IsPaid){
            return [];
        }

        if (ticket.payment === 'PAYMIXED'){
            return ticket.mixed.map((mixed) => ({
                method: mixed.payment || null,
                amount: this._round(mixed.amount || 0) ?? 0,
                given: null,
                change: null
            }));
        }

        if (!ticket.payment){
            return [];
        }

        const _total = ticket.priceinfo.tax.total || 0;
        const _given = (ticket.payment === 'PAYCASH') ? (ticket.given || null) : null;
        const _change = (_given != null) ? this._round(_given - _total) : null;

        return [{
            method: ticket.payment,
            amount: this._round(_total) ?? 0,
            given: _given != null ? this._round(_given) : null,
            change: _change
        }];
    }

    private DiscountAmounts(ticket: TicketView, total: number): TicketDataAmountsEntry {
        const _splits = ticket.priceinfo.tax.split || [];
        if (_splits.length === 0 || total === 0){
            return this.AmountsFromPrice(new PriceInfo(-Math.abs(total || 0), AppConstants.defaultTaxRate).price);
        }

        const _totalTaxed = _splits.reduce((acc, split) => acc + split.total, 0);
        const _taxes: TicketDataTaxEntry[] = [];
        let _accTotal = 0;
        let _accBase = 0;
        let _accAmount = 0;

        for (let idx = 0; idx < _splits.length; idx++){
            const _split = _splits[idx];
            const _share = (_totalTaxed === 0) ? 0 : (_split.total / _totalTaxed);
            let _lineTotal = -Math.abs(total) * _share;

            if (idx === (_splits.length - 1)){
                _lineTotal = -Math.abs(total) - _accTotal;
            }

            const _base = this._round(_lineTotal / (1 + (_split.rate / 100))) ?? 0;
            const _amount = this._round(_lineTotal - _base) ?? 0;
            const _roundedTotal = this._round(_lineTotal) ?? 0;

            _accTotal += _roundedTotal;
            _accBase += _base;
            _accAmount += _amount;

            _taxes.push({
                rate: _split.rate,
                base: _base,
                amount: _amount,
                total: _roundedTotal
            });
        }

        return {
            base: this._round(_accBase) ?? 0,
            amount: this._round(_accAmount) ?? 0,
            total: this._round(_accTotal) ?? 0,
            taxes: _taxes
        };
    }

    private AmountsFromPrice(price: PriceType): TicketDataAmountsEntry {
        const _taxes = this.TaxesFromPrice(price);
        return {
            base: this._round(price.tax.base) ?? 0,
            amount: this._round(price.tax.taxes) ?? 0,
            total: this._round(price.tax.total) ?? 0,
            taxes: _taxes
        };
    }

    private ZeroAmounts(): TicketDataAmountsEntry {
        return {
            base: 0,
            amount: 0,
            total: 0,
            taxes: [
                {
                    rate: 0,
                    base: 0,
                    amount: 0,
                    total: 0
                }
            ]
        };
    }

    private TaxesFromPrice(price: PriceType): TicketDataTaxEntry[] {
        const _split = price.tax.split || [];
        if (_split.length === 0){
            return [{
                rate: AppConstants.defaultTaxRate,
                base: this._round(price.tax.base) ?? 0,
                amount: this._round(price.tax.taxes) ?? 0,
                total: this._round(price.tax.total) ?? 0
            }];
        }

        return _split.map((entry) => ({
            rate: entry.rate,
            base: this._round(entry.base) ?? 0,
            amount: this._round(entry.taxes) ?? 0,
            total: this._round(entry.total) ?? 0
        }));
    }

    /*******************************/
    /* PRIVATE: MATH UTILITIES     */
    /*******************************/

    private _round(value: number | null | undefined): number | null {
        if (value == null){
            return null;
        }

        return Math.round(value * 100) / 100;
    }

    private _roundLineActivityEntry(entry: TicketDataActivityEntry): TicketDataActivityEntry {
        return {
            amount: this._round(entry.amount) ?? 0,
            income: this._round(entry.income) ?? 0,
        };
    }

    private _numornull(value: any): number | null {
        if ((value == null) || (value === '')){
            return null;
        }
        const _number = Number(value);
        return Number.isFinite(_number) ? _number : null;
    }

    private _strornull(value: any): string | null {
        const _text = String(value || '').trim();
        return _text.length > 0 ? _text : null;
    }

    private _countryCode(value: unknown): string | null {
        const _code = String(value || '').trim().toUpperCase();
        return _code.length > 0 ? _code : null;
    }

}
